import { io, type Socket } from 'socket.io-client';
import type { Message } from './types';
import type { RealtimeChatMessageEvent } from './realtime-mappers';

export interface SocketClientOptions {
  realtimeUrl: string;
  tenantId: string;
  jwt: string;
}

// Shapes MUST match apps/realtime/src/types/events.ts (single source of truth).
export interface AgentJoinedPayload {
  conversationId: string;
  agentId: string;
  agentName?: string;
}

export interface StatusChangedPayload {
  conversationId: string;
  status:
    | 'ai_handling'
    | 'awaiting_human'
    | 'assigned'
    | 'resolved'
    | 'abandoned';
}

export interface TypingPayload {
  conversationId: string;
  actor: 'agent' | 'ai' | 'customer';
  isTyping: boolean;
}

export type SocketHandlers = {
  onMessage?: (payload: RealtimeChatMessageEvent) => void;
  onAgentJoined?: (payload: AgentJoinedPayload) => void;
  onStatusChanged?: (payload: StatusChangedPayload) => void;
  onTyping?: (payload: TypingPayload) => void;
  onConnectChange?: (connected: boolean) => void;
  /** Called when we decide that realtime has failed — the caller should poll. */
  onFallback?: () => void;
};

/**
 * Thin wrapper over Socket.IO that keeps the widget agnostic to the lib.
 * Reconnection is handled by socket.io-client; after N failed attempts we
 * emit onFallback so the caller can switch to polling.
 */
export class RealtimeSocket {
  readonly #opts: SocketClientOptions;
  readonly #handlers: SocketHandlers;
  #socket: Socket | null = null;
  #failureCount = 0;
  #fallbackEmitted = false;
  static readonly #MAX_FAILURES = 4;

  constructor(opts: SocketClientOptions, handlers: SocketHandlers) {
    this.#opts = opts;
    this.#handlers = handlers;
  }

  connect(): void {
    if (this.#socket) return;

    const url = this.#opts.realtimeUrl.replace(/\/+$/, '');
    const namespace = `/t/${encodeURIComponent(this.#opts.tenantId)}`;
    this.#socket = io(`${url}${namespace}`, {
      auth: { jwt: this.#opts.jwt },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: RealtimeSocket.#MAX_FAILURES,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    this.#socket.on('connect', () => {
      this.#failureCount = 0;
      this.#handlers.onConnectChange?.(true);
    });

    this.#socket.on('disconnect', () => {
      this.#handlers.onConnectChange?.(false);
    });

    this.#socket.on('connect_error', () => {
      this.#failureCount += 1;
      if (this.#failureCount >= RealtimeSocket.#MAX_FAILURES && !this.#fallbackEmitted) {
        this.#fallbackEmitted = true;
        this.#handlers.onFallback?.();
      }
    });

    this.#socket.on('chat:message', (payload: RealtimeChatMessageEvent) => {
      this.#handlers.onMessage?.(payload);
    });
    this.#socket.on('chat:agent_joined', (payload: AgentJoinedPayload) => {
      this.#handlers.onAgentJoined?.(payload);
    });
    this.#socket.on('chat:status_changed', (payload: StatusChangedPayload) => {
      this.#handlers.onStatusChanged?.(payload);
    });
    this.#socket.on('chat:typing', (payload: TypingPayload) => {
      this.#handlers.onTyping?.(payload);
    });
  }

  disconnect(): void {
    this.#socket?.removeAllListeners();
    this.#socket?.disconnect();
    this.#socket = null;
  }

  get connected(): boolean {
    return this.#socket?.connected ?? false;
  }
}
